import { ColDef, GridApi, IRowNode } from 'ag-grid-community'

import {
  ensurePropIndex,
  type UnknownRow,
} from '@/components/Grid/utils/grid.utils'
import type {
  FormulaConfig,
  FormulaEvaluationContext,
  FormulaEvaluationResult,
  SupportedKeys,
} from '@/types'

import { shouldFreezeFormulaEvaluation } from './formula.helpers'
import { FORMULA_UPDATE_SOURCE, parseFormulaConfig } from './formula.config'
import {
  getManageFormulaChanges,
  recomputeManageFormulaFields,
} from './formula.runtime'

type FormulaRow = Record<string, unknown>

type FormulaCellEditorParams = {
  defaultValue?: unknown
  property?: SupportedKeys & { view?: 'references' | 'inventory' }
}

type FormulaView = 'references' | 'inventory'

type FormulaColumn = {
  field: string
  cellEditorParams: FormulaCellEditorParams
  config: FormulaConfig
}

const getCellStringValue = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object' || !('value' in value))
    return undefined
  const wrappedValue = (value as Record<string, unknown>).value
  if (wrappedValue === undefined || wrappedValue === null) return undefined
  return String(wrappedValue)
}

/** Read raw value from row (properties or top-level) for override detection. */
const getRawFormulaCellValue = (
  row: FormulaRow | undefined,
  field: string,
): string | undefined => {
  if (!row) return undefined
  ensurePropIndex(row as UnknownRow)
  const idx = (row as UnknownRow).__propIndex?.[field]
  const prop =
    idx != null && Array.isArray(row.properties)
      ? row.properties[idx]
      : undefined
  if (prop?.values != null) {
    const v = Array.isArray(prop.values) ? prop.values[0] : prop.values
    if (typeof v === 'string') return v
  }
  const top = row[field]
  return typeof top === 'string' ? top : getCellStringValue(top)
}

/** Resolve references then evaluate formula tokens. Shared by grid and import flows. */
export const computeFormulaResult = (
  data: FormulaRow,
  context: FormulaEvaluationContext,
  config: FormulaConfig,
): FormulaEvaluationResult => {
  const field = config.formulaField.referenceName || 'result'
  const outcome = recomputeManageFormulaFields({
    row: data,
    columns: [{ field, config }],
    context,
    allocateIncrements: false,
  })
  const state = outcome.fieldStates[field]
  const value = state?.status === 'valid' ? state.value : ''
  const status =
    state?.status === 'missing-inputs'
      ? 'missing'
      : (state?.status ?? 'invalid')
  return {
    value,
    status,
    displayValue: status === 'valid' && value ? value : '-',
    hasInputValues:
      Object.keys(data).some((key) => key !== 'properties') ||
      status !== 'missing',
    error: outcome.errors[field]?.some(
      (error) => error.code === 'missing-value',
    )
      ? `Missing values ${outcome.errors[field]
          .filter((error) => error.code === 'missing-value')
          .map((error) => ('referenceName' in error ? error.referenceName : ''))
          .join(', ')}`
      : undefined,
    entriesByRef: { [field]: { value, status } },
  }
}

const toFormulaColumn = <TRow extends FormulaRow>(
  colDef: ColDef<TRow>,
): FormulaColumn | undefined => {
  const field = typeof colDef.field === 'string' ? colDef.field : undefined
  if (!field) return undefined

  const rawCellEditorParams = colDef.cellEditorParams
  if (!rawCellEditorParams || typeof rawCellEditorParams !== 'object') {
    return undefined
  }
  const cellEditorParams = rawCellEditorParams as FormulaCellEditorParams
  if (cellEditorParams.property?.type !== 'FORMULA') return undefined

  const config = parseFormulaConfig(cellEditorParams.defaultValue)
  if (!config) return undefined

  return { field, cellEditorParams, config }
}

const isFormulaColumnVisibleInView = (
  column: FormulaColumn,
  view: FormulaView | undefined,
): boolean => {
  if (!view) return true

  const property = column.cellEditorParams.property
  if (!property) return true

  if (Array.isArray(property.visibleInViews)) {
    return property.visibleInViews.includes(view)
  }

  if (view === 'references') return property.hideInRef !== true
  if (view === 'inventory') return property.hideInInv !== true
  return true
}

const collectFormulaColumns = <TRow extends FormulaRow>(
  api: GridApi<TRow>,
  view?: FormulaView,
): FormulaColumn[] => {
  const columns = (api?.getColumnDefs() as ColDef<TRow>[]) || []
  return columns
    .map((colDef) => toFormulaColumn(colDef))
    .filter((column): column is FormulaColumn => Boolean(column))
    .filter((column) => isFormulaColumnVisibleInView(column, view))
}

const getFormulaOrder = (field?: string): number => {
  if (!field) return Number.MAX_SAFE_INTEGER
  if (field === 'formula') return 1
  const match = /^formula_(\d+)$/.exec(field)
  return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER
}

const getCanonicalLeafCreatedAt = (node: IRowNode<FormulaRow>): number => {
  const createdAt = node.data?.createdAt
  if (typeof createdAt !== 'string') return Number.POSITIVE_INFINITY

  const timestamp = Date.parse(createdAt)
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY
}

const getCanonicalLeafId = (node: IRowNode<FormulaRow>): string => {
  const id = node.data?.id
  if (typeof id === 'string') return id
  if (typeof id === 'number') return String(id)
  return ''
}

const compareCanonicalLeafNodes = (
  left: IRowNode<FormulaRow>,
  right: IRowNode<FormulaRow>,
) => {
  const leftCreatedAt = getCanonicalLeafCreatedAt(left)
  const rightCreatedAt = getCanonicalLeafCreatedAt(right)

  if (leftCreatedAt < rightCreatedAt) return -1
  if (leftCreatedAt > rightCreatedAt) return 1

  return getCanonicalLeafId(left).localeCompare(getCanonicalLeafId(right))
}

type ComputeFormulaValuesParams<TRow extends FormulaRow> = {
  api: GridApi<TRow>
  data: TRow
  context: FormulaEvaluationContext
  allRowData?: TRow[]
  force?: boolean
  formulaView?: FormulaView
  sourceField?: string
  sourceFields?: Iterable<string | undefined>
  includeUninitializedFormulas?: boolean
}

const normalizeFormulaSourceFields = (
  sourceField?: string,
  sourceFields?: Iterable<string | undefined>,
): Set<string> | undefined => {
  const fields = new Set<string>()
  if (typeof sourceField === 'string' && sourceField.length > 0) {
    fields.add(sourceField)
  }
  if (sourceFields) {
    Array.from(sourceFields).forEach((field) => {
      if (typeof field === 'string' && field.length > 0) fields.add(field)
    })
  }

  return fields.size > 0 ? fields : undefined
}

const doesReferenceDependOnSource = (
  referenceName: string,
  sourceField: string,
): boolean =>
  referenceName === sourceField ||
  referenceName.startsWith(`${sourceField}.`) ||
  sourceField.startsWith(`${referenceName}.`)

export const collectAffectedFormulaFields = (
  columns: Pick<FormulaColumn, 'field' | 'config'>[],
  sourceFields?: Iterable<string>,
): Set<string> => {
  const initialSourceFields = Array.from(sourceFields ?? []).filter(Boolean)
  if (initialSourceFields.length === 0) return new Set()

  const resolvedFields = new Set<string>(initialSourceFields)
  const affectedFormulaFields = new Set<string>()
  let hasChanged = true

  while (hasChanged) {
    hasChanged = false

    columns.forEach((column) => {
      if (affectedFormulaFields.has(column.field)) return

      const dependsOnSource =
        resolvedFields.has(column.field) ||
        column.config.fields.some((field) =>
          Array.from(resolvedFields).some((resolvedField) =>
            doesReferenceDependOnSource(field.referenceName, resolvedField),
          ),
        )

      if (!dependsOnSource) return

      affectedFormulaFields.add(column.field)
      resolvedFields.add(column.field)
      hasChanged = true
    })
  }

  return affectedFormulaFields
}

const isUninitializedFormulaValue = (value: string | undefined): boolean =>
  value === undefined || value.length === 0

/** Compute all formula field values for a given row, without updating the grid */
export const computeFormulaValues = <TRow extends FormulaRow>({
  api,
  data,
  context,
  allRowData,
  force = false,
  formulaView,
  sourceField,
  sourceFields,
  includeUninitializedFormulas = false,
}: ComputeFormulaValuesParams<TRow>): Record<string, string> => {
  const formulaColumns = collectFormulaColumns(api)
  const formulaSourceFields = normalizeFormulaSourceFields(
    sourceField,
    sourceFields,
  )
  const affectedFormulaFields = formulaSourceFields
    ? collectAffectedFormulaFields(formulaColumns, formulaSourceFields)
    : undefined

  if (affectedFormulaFields?.size === 0 && !includeUninitializedFormulas) {
    return {}
  }

  const targets = new Set(
    formulaColumns
      .filter((column) => {
        if (!isFormulaColumnVisibleInView(column, formulaView)) return false
        const { property } = column.cellEditorParams
        if (property?.isRef && property.view !== 'references') return false
        return (
          !affectedFormulaFields ||
          affectedFormulaFields.has(column.field) ||
          (includeUninitializedFormulas &&
            isUninitializedFormulaValue(
              getRawFormulaCellValue(data, column.field),
            ))
        )
      })
      .map((column) => column.field),
  )
  if (targets.size === 0) return {}
  const outcome = recomputeManageFormulaFields({
    row: data,
    columns: formulaColumns.filter(
      (column) =>
        !column.cellEditorParams.property?.isRef ||
        column.cellEditorParams.property.view === 'references',
    ),
    context,
    allRowData,
    getIncrementRows: () => {
      const rows: TRow[] = []
      api?.forEachNode((node) => {
        if (node.data) rows.push(node.data)
      })
      return rows
    },
    force,
    targetFields: targets,
  })
  return Object.fromEntries(
    Object.entries(getManageFormulaChanges(outcome)).filter(
      ([field, value]) => value !== getRawFormulaCellValue(data, field),
    ),
  )
}

type EvaluateAndApplyFormulasParams<TRow extends FormulaRow> = {
  api: GridApi<TRow>
  node: IRowNode<TRow> | null
  context: FormulaEvaluationContext
  allRowData?: TRow[]
  force?: boolean
  formulaView?: FormulaView
  sourceField?: string
  sourceFields?: Iterable<string | undefined>
  includeUninitializedFormulas?: boolean
}

export const evaluateAndApplyFormulas = <TRow extends FormulaRow>({
  api,
  node,
  context,
  allRowData,
  force = false,
  formulaView,
  sourceField,
  sourceFields,
  includeUninitializedFormulas,
}: EvaluateAndApplyFormulasParams<TRow>): {
  changed: boolean
  computedFields: Record<string, string>
} => {
  const data = node?.data
  if (!data) {
    return {
      changed: false,
      computedFields: {},
    }
  }

  const computedFields = computeFormulaValues({
    api,
    data,
    context,
    allRowData,
    force,
    formulaView,
    sourceField,
    sourceFields,
    includeUninitializedFormulas,
  })

  const changedColumns = Object.keys(computedFields)
  if (changedColumns.length === 0 || !node) {
    return {
      changed: false,
      computedFields,
    }
  }

  changedColumns.forEach((columnKey) => {
    const nextValue = computedFields[columnKey]
    node.setDataValue(columnKey, nextValue, FORMULA_UPDATE_SOURCE)
  })

  return {
    changed: true,
    computedFields,
  }
}

export const syncGroupStableFormulaValues = <TRow extends FormulaRow>({
  api,
  nodes,
  context,
  sourceField,
  sourceFields,
  includeUninitializedFormulas,
}: {
  api: GridApi<TRow>
  nodes: Array<IRowNode<TRow> | null | undefined>
  context: FormulaEvaluationContext
  sourceField?: string
  sourceFields?: Iterable<string | undefined>
  includeUninitializedFormulas?: boolean | ((node: IRowNode<TRow>) => boolean)
}): Map<IRowNode<TRow>, Record<string, string>> => {
  const changedFieldsByNode = new Map<IRowNode<TRow>, Record<string, string>>()
  const leafNodes = nodes.filter(
    (node): node is IRowNode<TRow> => !node?.group && Boolean(node?.data),
  )
  if (leafNodes.length === 0) return changedFieldsByNode

  const shouldIncludeUninitializedFormulas = (node: IRowNode<TRow>): boolean =>
    typeof includeUninitializedFormulas === 'function'
      ? includeUninitializedFormulas(node)
      : Boolean(includeUninitializedFormulas)

  const [leaderNode, ...followerNodes] = [...leafNodes].sort((left, right) =>
    compareCanonicalLeafNodes(
      left as IRowNode<FormulaRow>,
      right as IRowNode<FormulaRow>,
    ),
  )

  const leaderResult = evaluateAndApplyFormulas({
    api,
    node: leaderNode,
    context,
    sourceField,
    sourceFields,
    includeUninitializedFormulas:
      shouldIncludeUninitializedFormulas(leaderNode),
  })
  if (leaderResult.changed) {
    changedFieldsByNode.set(leaderNode, leaderResult.computedFields)
  }

  const leaderData = leaderNode.data
  if (!leaderData) return changedFieldsByNode

  const formulaColumns = collectFormulaColumns(api).sort(
    (a, b) => getFormulaOrder(a.field) - getFormulaOrder(b.field),
  )
  const formulaSourceFields = normalizeFormulaSourceFields(
    sourceField,
    sourceFields,
  )
  const affectedFormulaFields = formulaSourceFields
    ? collectAffectedFormulaFields(formulaColumns, formulaSourceFields)
    : new Set(formulaColumns.map((column) => column.field))
  Object.keys(leaderResult.computedFields).forEach((field) =>
    affectedFormulaFields.add(field),
  )

  const sharedFormulaValues = formulaColumns.flatMap((column) => {
    if (!affectedFormulaFields.has(column.field)) return []

    const rawValue = getRawFormulaCellValue(leaderData, column.field)
    if (
      !shouldFreezeFormulaEvaluation({
        rawValue,
        formula: column.config.formulaField.formula,
      })
    ) {
      return []
    }

    return rawValue ? [[column.field, rawValue] as const] : []
  })

  followerNodes.forEach((node) => {
    const changedFields: Record<string, string> = {}

    sharedFormulaValues.forEach(([field, value]) => {
      if (getRawFormulaCellValue(node.data, field) === value) return
      node.setDataValue(field, value, FORMULA_UPDATE_SOURCE)
      changedFields[field] = value
    })

    const result = evaluateAndApplyFormulas({
      api,
      node,
      context,
      sourceField,
      sourceFields,
      includeUninitializedFormulas: shouldIncludeUninitializedFormulas(node),
    })

    Object.assign(changedFields, result.computedFields)
    if (Object.keys(changedFields).length > 0) {
      changedFieldsByNode.set(node, changedFields)
    }
  })

  return changedFieldsByNode
}

export const recomputeFormulaOnClick = <TRow extends FormulaRow>({
  api,
  node,
  colDef,
  context,
}: {
  api: GridApi<TRow>
  node: IRowNode<TRow> | null
  colDef: ColDef<TRow>
  context: FormulaEvaluationContext
}): {
  isFormulaCell: boolean
  changed: boolean
  computedFields: Record<string, string>
} => {
  if (!toFormulaColumn(colDef)) {
    return {
      isFormulaCell: false,
      changed: false,
      computedFields: {},
    }
  }

  return {
    isFormulaCell: true,
    ...evaluateAndApplyFormulas({
      api,
      node,
      context,
    }),
  }
}
